import {
  ExternalLink,
  Map as MapIcon,
  Network,
  Sparkles,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import type { SourceBadge } from '@/types/api';

import { useChatStore } from '../store/chatStore';

interface Props {
  quotaRemaining: number | null;
}

/**
 * Right-rail context panel — surfaces what's grounded the current
 * thread (cited sources) plus quick jumps to related surfaces and
 * the live quota counter. Reactive to the chat store so it refreshes
 * as new assistant turns land.
 */
export function ContextPanel({ quotaRemaining }: Props) {
  const { t } = useTranslation(['app', 'common']);
  const messages = useChatStore((s) => s.messages);

  const sources = useMemo(() => collectSources(messages), [messages]);

  return (
    <div className="space-y-4">
      <header>
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" aria-hidden />
          <h2 className="text-h3 text-brand-900">
            {t('app:chat.context.title', 'Context')}
          </h2>
        </div>
        <p className="mt-0.5 text-small text-slate-500">
          {t(
            'app:chat.context.subtitle',
            'Sources cited in this thread + quick jumps.',
          )}
        </p>
      </header>

      <SourcesSection sources={sources} />
      <QuickLinks />

      {quotaRemaining != null && (
        <section>
          <h3 className="mb-1 text-h4 text-slate-700">
            {t('app:chat.context.quotaTitle', 'Daily quota')}
          </h3>
          <div className="flex items-baseline justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2 text-small">
            <span className="text-slate-500">
              {t('app:chat.context.quotaLabel', 'Queries remaining today')}
            </span>
            <span
              className={cn(
                'font-semibold',
                quotaRemaining === 0 ? 'text-red-700' : 'text-brand-900',
              )}
            >
              {quotaRemaining}
            </span>
          </div>
        </section>
      )}
    </div>
  );
}

// ─── Sources ────────────────────────────────────────────────────

interface SourceEntry extends SourceBadge {
  /** How many assistant turns in this thread cited this source. */
  count: number;
}

function collectSources(
  messages: { sources?: SourceBadge[] }[],
): SourceEntry[] {
  const byId = new Map<string, SourceEntry>();
  for (const m of messages) {
    for (const src of m.sources ?? []) {
      const key = src.source_id;
      const existing = byId.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        byId.set(key, { ...src, count: 1 });
      }
    }
  }
  return Array.from(byId.values()).sort((a, b) => b.count - a.count);
}

function SourcesSection({ sources }: { sources: SourceEntry[] }) {
  const { t } = useTranslation('app');
  if (sources.length === 0) {
    return (
      <section>
        <h3 className="mb-1 text-h4 text-slate-700">
          {t('chat.context.sourcesTitle', 'Sources')}
        </h3>
        <p className="text-small text-slate-500">
          {t(
            'chat.context.sourcesEmpty',
            'Sources cited by the assistant will appear here.',
          )}
        </p>
      </section>
    );
  }
  return (
    <section>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-h4 text-slate-700">
          {t('chat.context.sourcesTitle', 'Sources')}
        </h3>
        <span className="rounded bg-slate-100 px-1.5 text-small text-slate-500">
          {sources.length}
        </span>
      </div>
      <ul className="space-y-1">
        {sources.slice(0, 20).map((s) => (
          <SourceRow key={s.source_id} entry={s} />
        ))}
      </ul>
      {sources.length > 20 && (
        <p className="mt-1 text-small text-slate-400">
          {t('chat.context.sourcesMore', { count: sources.length - 20 })}
        </p>
      )}
    </section>
  );
}

function SourceRow({ entry }: { entry: SourceEntry }) {
  const host = hostnameOf(entry.source_url);
  const pubDate = entry.publication_date
    ? new Date(entry.publication_date).toLocaleDateString()
    : null;
  return (
    <li>
      <a
        href={entry.source_url}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-start gap-2 rounded px-1 py-0.5 text-small hover:bg-slate-100"
        title={entry.source_url}
      >
        <ExternalLink
          className="mt-0.5 h-3 w-3 shrink-0 text-slate-400 group-hover:text-accent"
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-slate-800">{host}</span>
          {pubDate && (
            <span className="text-[10px] text-slate-400">{pubDate}</span>
          )}
        </span>
        {entry.count > 1 && (
          <span className="shrink-0 rounded bg-slate-100 px-1 text-[10px] text-slate-500">
            ×{entry.count}
          </span>
        )}
      </a>
    </li>
  );
}

function hostnameOf(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

// ─── Quick links ────────────────────────────────────────────────

function QuickLinks() {
  const { t } = useTranslation('app');
  return (
    <section>
      <h3 className="mb-1 text-h4 text-slate-700">
        {t('chat.context.jumpTitle', 'Jump to')}
      </h3>
      <div className="grid grid-cols-2 gap-1.5">
        <JumpCard
          to="/app/map"
          icon={<MapIcon className="h-3.5 w-3.5" aria-hidden />}
          label={t('chat.context.jumpMap', 'Map')}
        />
        <JumpCard
          to="/app/explorer"
          icon={<Network className="h-3.5 w-3.5" aria-hidden />}
          label={t('chat.context.jumpExplorer', 'Explorer')}
        />
      </div>
    </section>
  );
}

function JumpCard({
  to,
  icon,
  label,
  className,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-1.5 rounded border border-slate-200 px-2 py-1.5 text-small text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-900',
        className,
      )}
    >
      {icon}
      {label}
    </Link>
  );
}
